import {
  CellDoubleClickedEvent,
  CellKeyDownEvent,
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GridOptions,
  ICellEditorParams,
  RowNode,
} from '@ag-grid-community/core'
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core'
import { FormControl, ValidationErrors } from '@angular/forms'
import {
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import { RootState, selectIsInternalUser } from '@helios/state-management'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams, resizeColumns } from '@northfork/ag-grid'
import {
  InlineEditDataType,
  InlineEditorParams,
  getInlineEditColumnType,
} from '@northfork/ag-grid-angular'
import { ISODateFormat, toISOFormat } from '@northfork/basics'
import { InlineEditDisplayOptions } from '@northfork/ng-basics'
import { Subject, of } from 'rxjs'
import { catchError, take, takeUntil, tap } from 'rxjs/operators'
import { Inventory, inventories } from '../../../../model/inventory-type.model'
import { Basis, BasisType } from '../../../../model/mvt-basis.model'
import {
  PricingRequest,
  PricingResponse,
} from '../../../../model/pricing.model'
import { Security } from '../../../../model/security.model'
import {
  AccountDetails,
  CombinedAccountDetails,
} from '../../../../model/single-transaction/account-details.model'
import {
  DirectTransfer,
  GetBalanceUnitsPayload,
  TransferMappingModel,
  TransferPositionData,
  transferField,
} from '../../../../model/single-transaction/direct-transfer/direct-transfer-transaction.model'
import {
  normalExchangeTypes,
  statExchangeTypes,
} from '../../../../model/single-transaction/direct-transfer/exchange-type.model'
import { MvtData } from '../../../../model/single-transaction/market-value-transfer.model'
import { SleeveSimpleAccounts } from '../../../../model/single-transaction/sleeve-simple-accounts.model'
import { TranLink } from '../../../../model/single-transaction/tran-link.model'
import {
  EXITS,
  TransactionTypeEnum,
  TransactionTypeKey,
} from '../../../../model/transaction-type.model'
import { CopyService } from '../../../../services/copy.service'
import { CreateEditTransactionService } from '../../../../services/create-edit-transaction.service'
import { PasteService } from '../../../../services/single-transaction/paste.service'
import { Calculators } from '../../../../util/calculators.util'
import {
  ACCOUNT_REQUIRED_ERROR,
  ACCOUNT_REQUIRED_FIELD_WARNING,
  addWarningDetails,
  clearAllInvalidations,
  emptySelectedCellsOnValidKeyPress,
  fillHandleDoubleClickHandlerSingleDialog,
  focusFieldRowEditor,
  formatNumberWithThousandSeparator,
  handleValidation,
  internalAssetIdFilterGridOptions,
} from '../../../../util/common-utils'
import {
  generateTransferCreateMapping,
  generateTransferEditMapping,
  generateTransferFromPosition,
  getCollateralBroker,
  getExitFeeReceivableRow,
} from '../../../../util/direct-transfer-data-mapper-util'
import { MvtBasesInOut } from './../../../../model/mvt-basis-in-out.model'

@Component({
  selector: 'hs-transfer-in-out-form',
  templateUrl: './transfer-in-out-form.component.html',
  styleUrls: ['./transfer-in-out-form.component.scss'],
  providers: [PasteService, CopyService],
})
export class TransferInOutFormComponent implements AfterViewInit, OnDestroy {
  private _aggregateAccountUnresolved = false
  rowNodeMap = new Map<string, RowNode>()
  gridApi?: AgGridApi
  securityId: number | undefined
  private readonly colDefs: ColDef[] = this.createColumnDefs()
  gridOptions: GridOptions = this.createGridOptions()
  entryDate: string | undefined
  rowData: TransferMappingModel[] = generateTransferCreateMapping()
  isInternalUser = false
  private readonly destroySubject$ = new Subject<void>()

  basis = new FormControl()
  errorFields: string[] = []
  noteWarning: ValidationErrors | null = null
  note = new FormControl('')
  oldCusip = ''

  private _transaction: DirectTransfer | undefined
  private _positionData: TransferPositionData | undefined
  private _cusip: string | undefined
  private _accountDetails: AccountDetails | undefined
  private _sleeveSimpleAccount: SleeveSimpleAccounts[] = []
  private _destinationAccountDetail: AccountDetails | undefined
  private _security: Security | undefined

  private _isSourceCollateral = false

  private _isDestinationCollateral = false

  @Input()
  set security(security: Security | undefined) {
    this._security = security
    if (this.gridApi) this.adjustExitFeeReceivable()
  }
  get security() {
    return this._security
  }

  @Input()
  set isSourceCollateral(isSourceCollateral: boolean) {
    this._isSourceCollateral = isSourceCollateral
    if (this.gridApi) this.adjustCollateralBroker()
  }

  @Input()
  set isDestinationCollateral(isDestinationCollateral: boolean) {
    this._isDestinationCollateral = isDestinationCollateral
    if (this.gridApi) this.adjustCollateralBroker()
  }

  @Input()
  set destinationAccountDetail(
    destinationAccountDetail: AccountDetails | undefined,
  ) {
    if (!destinationAccountDetail) {
      return
    }
    this._destinationAccountDetail = destinationAccountDetail
    this.updateDestinationAccount()
  }

  @Input()
  set transaction(transaction: DirectTransfer | undefined) {
    if (transaction != null) {
      this._transaction = transaction
      this.updateTransactionData(transaction)
    }
  }

  get transaction() {
    return this._transaction
  }

  @Input() set transferPositionData(
    positionData: TransferPositionData | undefined,
  ) {
    if (positionData != null) {
      Promise.resolve().then(() => {
        this._positionData = positionData
        this.rowData = generateTransferFromPosition(positionData)
        this.oldCusip = positionData.assetId
        this.loadPricingInfo()
      })
    }
  }

  get transferPositionData() {
    return this._positionData
  }

  @Input()
  set cusip(cusip: string | undefined) {
    this._cusip = cusip
  }

  @Input()
  set tranLinkInfo(tranLinkInfo: TranLink | undefined) {
    if (tranLinkInfo != null) {
      this.destinationAccountId = tranLinkInfo.destAccountId
    }
  }

  @Output()
  currentSecurityId = new EventEmitter<number | undefined>()

  @Output()
  editingStarted = new EventEmitter<void>()
  @Output()
  editingStopped = new EventEmitter<void>()

  @Input()
  sourceAccountId?: number

  @Input()
  mvtDataMap?: { [key: number]: MvtData }

  private _editMode = false
  @Input()
  set editMode(editMode: boolean) {
    this._editMode = editMode
  }
  get editMode() {
    return this._editMode
  }
  @Output()
  inOutMvtBases = new EventEmitter<MvtBasesInOut>()

  @Input()
  destinationAccountId?: number

  @Input()
  sessionId?: string

  @Input()
  set reconDate(reconDate: Date | undefined) {
    this.entryDate = reconDate?.toString()
    this.setRowValueById(toISOFormat(reconDate) + '', 'entryDate')
  }

  @Input() set accountDetails(accountDetails: AccountDetails | undefined) {
    if (accountDetails) {
      this._accountDetails = accountDetails
      this.updateSourceAccountDetails()
    }
  }
  get accountDetails() {
    return this._accountDetails
  }

  inventories: Inventory[] = inventories
  exchangeTypeValues = normalExchangeTypes

  tranType: TransactionTypeKey = 'TRANSFER_OUT'

  dateFormat = ISODateFormat

  @Output()
  dataMap = new EventEmitter<Map<string, RowNode>>()

  @Output()
  basisSelected = new EventEmitter<Basis[]>()

  @Output()
  valueChanged = new EventEmitter<CellValueChangedEvent>()

  @Output()
  sleeveAccountDetails = new EventEmitter<CombinedAccountDetails>()

  @Output()
  errorDetails = new EventEmitter<unknown>()

  @Output()
  errorHeader = new EventEmitter<string>()

  @Output()
  securityChanged = new EventEmitter<Security | undefined>()

  @Input() set sleeveSimpleAccount(sleeveAccounts: SleeveSimpleAccounts[]) {
    if (sleeveAccounts?.length > 0) {
      this._sleeveSimpleAccount = sleeveAccounts
      this.setAggAccountDisplayName()
      // Accounts just arrived; if we're waiting on a sub-account choice, open the
      // picker now that it has options to show.
      if (this._aggregateAccountUnresolved && this.gridApi?.api) {
        focusFieldRowEditor(this.gridApi.api, 'sourceAccount')
      }
    }
  }

  get sleeveSimpleAccount() {
    return this._sleeveSimpleAccount
  }

  constructor(
    private readonly createEditTransactionService: CreateEditTransactionService,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
    private readonly store: Store<RootState>,
  ) {
    this.store
      .select(selectIsInternalUser)
      .pipe(take(1))
      .subscribe(isInternalUser => {
        this.isInternalUser = isInternalUser ?? false
        this.gridApi?.api.onFilterChanged()
      })
  }

  /**
   * PVAL-25738: external users must not see the internal Asset ID rows when
   * editing/viewing an existing transaction.
   */
  private get hideInternalAssetIdRows(): boolean {
    return !this.isInternalUser && this.editMode
  }

  ngAfterViewInit(): void {
    this.subscribeFormControls()
    this.dataMap.emit(this.rowNodeMap)
  }

  onBasisUpdate(basis: Basis[]) {
    if (basis.some(b => b.basisType === BasisType.STAT)) {
      this.exchangeTypeValues = statExchangeTypes
    } else {
      this.exchangeTypeValues = normalExchangeTypes
    }

    this.setRowValueById(statExchangeTypes[0].name, 'exchangeInOutType')
  }

  ngOnDestroy() {
    this.pasteService.destroy()
    this.copyService.destroy()
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  setInOutMvtBases(inOutMvtBases?: MvtBasesInOut) {
    this.inOutMvtBases.emit(inOutMvtBases)
  }

  subscribeFormControls() {
    this.basis.valueChanges
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(basis => {
        this.basisSelected.emit(basis)
      })
  }

  updatePricingInfo(pricingInfo: PricingResponse | null) {
    if (pricingInfo == null) {
      return
    }

    this.currentSecurityId.emit(pricingInfo?.securityId)
    this.securityId = pricingInfo.securityId
    this.getBalanceUnits()

    this.setRowValueById(pricingInfo.price?.toString() ?? '', 'transferPrice')
    this.valueChanged.emit()
  }

  setRowValueById(value: string, id: string) {
    const rowField = this.rowData.find(row => row.id == id)
    if (rowField) {
      rowField.value = value
      this.gridApi?.api.refreshCells()
    }
  }

  get colDef() {
    return this.colDefs
  }

  private createColumnDefs(): ColDef[] {
    return [
      {
        field: transferField('isEditable'),
        hide: true,
      },
      {
        field: transferField('errorMessage'),
        hide: true,
      },
      {
        field: transferField('validityStatus'),
        headerName: '',
        valueGetter: () => '',
        cellClass: params => {
          return params.data.validityStatus === 'valid'
            ? 'mdi mdi-check-circle text-success'
            : params.data.validityStatus === 'invalid'
            ? 'mdi mdi-close-circle-outline text-danger'
            : 'mdi mdi-alert-circle-outline text-warning'
        },
        tooltipField: 'errorMessage',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessage != null && params.data.errorMessage != '',
        } as NfTooltipParams,
        maxWidth: heliosDefaultColWidth.ValidityStatusCol,
      },
      {
        field: transferField('field'),
        headerName: 'Field',
        maxWidth: 200,
      },
      {
        field: transferField('value'),
        headerName: 'Value',
        type: getInlineEditColumnType(),
        cellEditorParams: (params: ICellEditorParams) =>
          this.editInlineColumns(params),
        editable: (x: EditableCallbackParams) => this.editable(x),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        valueFormatter: params =>
          formatNumberWithThousandSeparator(params.data.type, params.value),
      },
    ]
  }

  getTypeSign(): number {
    const tranSign = EXITS.includes(TransactionTypeEnum[this.tranType]) ? -1 : 1
    const inventorySign =
      this.rowNodeMap.get('inventoryType')?.data.value == 'SHORT' ? -1 : 1
    return tranSign * inventorySign
  }

  getBalanceUnits() {
    if (
      this.editMode ||
      this.securityId == null ||
      this.entryDate == null ||
      this.sourceAccountId == null
    )
      return

    const inventoryType =
      this.gridApi?.api.getRowNode('inventoryType')?.data.value
    const collateralBroker =
      this.gridApi?.api.getRowNode('collateralBroker')?.data.value

    const getBalanceUnitsPayload: GetBalanceUnitsPayload = {
      date: this.entryDate,
      securityId: this.securityId,
      accountId: this.sourceAccountId,
      ...(inventoryType && { inventoryType }),
      ...(collateralBroker && { collateralBroker }),
    }

    this.createEditTransactionService
      .getBalanceUnits(getBalanceUnitsPayload)
      .pipe(
        take(1),

        tap(response => {
          this.setRowValueById(
            response.balanceUnits.toString() ?? '',
            'balanceUnits',
          )
          this.setRowValueById(
            response && response.balanceUnits < 0 ? 'SHORT' : 'LONG',
            'inventoryType',
          )
          this.valueChanged.emit()
        }),
        catchError(error => {
          this.errorDetails.emit(error.error)
          this.errorHeader.emit('Error fetching balance units')
          return of(null)
        }),
      )
      .subscribe()
  }

  setErrorDetails(errorDetails: unknown) {
    this.errorDetails.emit(errorDetails)
  }

  setErrorHeader(errorHeader: string | undefined) {
    this.errorHeader.emit(errorHeader)
  }

  // When the parent has an unresolved aggregate account, defer validation and flag the
  // source-account picker red until a concrete sub-account is chosen.
  @Input() set aggregateAccountUnresolved(value: boolean) {
    this._aggregateAccountUnresolved = value
    if (value) {
      // Mark the account row as the blocker until a concrete sub-account is chosen.
      // Mutate rowData (not a grid node) so it renders even if the grid isn't ready yet.
      const sourceAccountRowField = this.rowData?.find(
        row => row.id == 'sourceAccount',
      )
      if (sourceAccountRowField) {
        sourceAccountRowField.validityStatus = 'invalid'
        sourceAccountRowField.errorMessage = ACCOUNT_REQUIRED_ERROR
      }
    }
    this.gridApi?.api.refreshCells({ force: true })
    // Only open the picker once its options have loaded; otherwise the select
    // opens stuck on its (static) loading state.
    if (value && this.sleeveSimpleAccount.length > 0 && this.gridApi?.api) {
      focusFieldRowEditor(this.gridApi.api, 'sourceAccount')
    }
  }
  get aggregateAccountUnresolved(): boolean {
    return this._aggregateAccountUnresolved
  }

  editable(params: EditableCallbackParams): boolean {
    const tableNode = params.api?.getRowNode(params.data['id'])
    if (tableNode) {
      this.rowNodeMap.set(params.data['id'], tableNode)
    }
    // Fields stay editable before an account is picked; validation is deferred instead, and
    // edited rows are flagged until the account is resolved (see onColumnValueChanged).
    return params.data.isEditable
  }

  editInlineColumns(params: ICellEditorParams): InlineEditorParams {
    switch (params.data.id) {
      case 'entryDate':
        return {
          dataType: InlineEditDataType.Date,
        }
      case 'sourceAccount':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: this.sleeveSimpleAccount,
            bindValue: 'displayLabel',
            bindLabel: 'displayLabel',
            loading: this.sleeveSimpleAccount.length == 0,
          },
        }
      case 'destAccountId':
        return {
          dataType: InlineEditDataType.Number,
        }
      case 'destAccountName':
      case 'exchangeInOutType':
      case 'oldCusip':
        return {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        }
      case 'rate':
      case 'transferPrice':
      case 'units':
      case 'prepaidInterest':
      case 'interestDue':
      case 'exitFeeReceivable':
        return {
          dataType: InlineEditDataType.Number,
          options: {
            format: '1.2-16',
          },
          disablePopover: true,
        }
      case 'inventoryType':
        return {
          dataType: InlineEditDataType.Select,
          options: {
            items: inventories,
            bindValue: 'id',
            bindLabel: 'id',
          },
        }
      case 'isConversion':
        return {
          dataType: InlineEditDataType.Checkbox,
        }
      default:
        return {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        }
    }
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      ...internalAssetIdFilterGridOptions(
        () => this.hideInternalAssetIdRows,
        () => this.rowNodeMap,
      ),
      onGridReady: event => {
        this.gridApi = event
        resizeColumns(event.api, event.columnApi)
        this.copyService.registerGrid(event.api)
        this.pasteService.registerGrid(event.api, event.columnApi)
        this.adjustCollateralBroker()
        this.adjustExitFeeReceivable()
      },
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        minWidth: heliosDefaultColWidth.ValidityStatusCol,
        sortable: false,
      },
      sideBar: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      suppressMultiRangeSelection: false,
      enableFillHandle: true,
      fillHandleDirection: 'y',
      onCellEditingStarted: () => {
        this.editingStarted.emit()
      },
      onCellEditingStopped: () => {
        this.editingStopped.emit()
      },
      onCellKeyDown: (event: CellKeyDownEvent) => {
        if (event.api.getEditingCells().length == 0) {
          emptySelectedCellsOnValidKeyPress(
            (event.event as KeyboardEvent).key,
            event.api,
          )
        }
      },
      getRowNodeId: ({ id }: TransferMappingModel) => id,
      columnDefs: this.colDefs,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onFirstDataRendered: (params: any) => {
        params.api.sizeColumnsToFit()
      },
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        if (
          event.column.getColId() === 'value' &&
          event.data.id == 'balanceUnits' &&
          event.data.value != '--'
        ) {
          const units = Math.abs(event.data.value) * this.getTypeSign()
          this.setRowValueById(Math.abs(units).toString(), 'units')
          this.valueChanged.emit()
        } else {
          const element = event.event?.target as HTMLElement
          if (element.className == 'ag-fill-handle') {
            fillHandleDoubleClickHandlerSingleDialog(event.api)
          } else {
            event.api.startEditingCell({
              rowIndex: event.rowIndex ?? 0,
              colKey: event.column.getColId(),
            })
          }
        }
      },
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
    }
  }

  async onColumnValueChanged(event: CellValueChangedEvent) {
    const currentInventoryType = this.rowData?.find(
      row => row.id == 'inventoryType',
    )?.value
    const currentUnit = this.rowData?.find(row => row.id == 'units')?.value
    if (
      event.newValue === event.oldValue ||
      event.column.getColId() != 'value'
    ) {
      return
    }
    if (event.data['id'] == 'oldCusip') {
      this.oldCusip = event.data['value']
      this.loadPricingInfo()
    } else if (event.data['id'] == 'destAccountId') {
      this.destinationAccountId = Number(event.data['value'])
    } else if (event.data['id'] == 'entryDate') {
      this.entryDate = event.data['value']
      this.loadPricingInfo()
    } else if (event.data['id'] == 'sourceAccount') {
      await this.loadAggAccountDetails(event.data['value'])
      this.loadPricingInfo()
      this.getBalanceUnits()
    } else if (event.data['id'] == 'units' && currentInventoryType != 'ISSUE') {
      this.setRowValueById(
        Math.abs(Number(event.data['value'])).toString(),
        'units',
      )
    } else if (event.data['id'] == 'inventoryType') {
      this.getBalanceUnits()
      if (currentInventoryType != 'ISSUE') {
        this.setRowValueById(Math.abs(Number(currentUnit)).toString(), 'units')
      }
    } else if (event.data['id'] == 'collateralBroker') {
      this.getBalanceUnits()
    }
    this.dataMap.emit(this.rowNodeMap)
    if (
      this.aggregateAccountUnresolved &&
      event.data['id'] != 'sourceAccount'
    ) {
      // No account yet: flag the edited field instead of validating it.
      addWarningDetails(event.node, ACCOUNT_REQUIRED_FIELD_WARNING)
      event.api.refreshCells({ force: true, rowNodes: [event.node] })
    } else {
      this.valueChanged.emit(event)
    }
  }

  loadPricingInfo() {
    if (
      this.sessionId == null ||
      this.entryDate == null ||
      this.entryDate === '' ||
      this.sourceAccountId == null ||
      this.oldCusip === ''
    ) {
      return
    }
    const inventoryType =
      this.gridApi?.api.getRowNode('inventoryType')?.data.value
    const collateralBroker =
      this.gridApi?.api.getRowNode('collateralBroker')?.data.value
    this.errorDetails.emit('')
    const pricingRequest: PricingRequest = {
      entryDate: this.entryDate,
      accountId: this.sourceAccountId,
      cusip: this.oldCusip,
      ...(inventoryType && { inventoryType }),
      ...(collateralBroker && { collateralBroker }),
    }

    this.createEditTransactionService
      .loadPricingInformation(pricingRequest)
      .pipe(
        take(1),
        tap(pricingInfo => this.updatePricingInfo(pricingInfo)),
        catchError(error => {
          this.errorDetails.emit(error.error)
          this.errorHeader.emit('Error fetching pricing information')
          return of(null)
        }),
      )
      .subscribe()

    this.loadSecurityDetails()
  }

  private loadSecurityDetails() {
    // The security lookup is account-scoped; defer it until an account is resolved.
    if (this.aggregateAccountUnresolved) {
      return
    }
    if (this.entryDate == null || this.entryDate === '' || !this.oldCusip) {
      return
    }
    // sourceAccountId should be set here; if it isn't, the backend treats the 0 as
    // "no account" and uses the global (clientId=1) security scope, not account 0.
    this.createEditTransactionService
      .fetchSecurityDetailsFromCusip(
        this.oldCusip,
        this.entryDate,
        this.sourceAccountId ?? 0,
      )
      .pipe(
        take(1),
        tap(security => {
          this._security = security
          this.securityChanged.emit(security)
          this.adjustExitFeeReceivable()
        }),
        catchError(() => of(null)),
      )
      .subscribe()
  }

  updateTransactionData(transferTransaction: DirectTransfer) {
    this.rowData = generateTransferEditMapping(
      transferTransaction,
      this._destinationAccountDetail,
    )
    if (this._cusip) {
      this.setRowValueById(this._cusip, 'oldCusip')
    }
    if (this.destinationAccountId) {
      this.setRowValueById(
        this.destinationAccountId.toString(),
        'destAccountId',
      )
    }
    this.updateSourceAccountDetails()
  }

  updateSourceAccountDetails() {
    this.setRowValueById(
      this.accountDetails?.accountId + ' - ' + this.accountDetails?.accountName,
      'sourceAccount',
    )
  }
  updateDestinationAccount() {
    this.setRowValueById(
      this._destinationAccountDetail?.accountId +
        '-' +
        this._destinationAccountDetail?.accountName,
      'destinationAccount',
    )
  }

  handleValidation(
    rowNode: RowNode,
    validationState: string,
    validationMessage: string,
  ) {
    if (this.gridApi)
      handleValidation(rowNode, validationState, validationMessage)
  }

  async loadAggAccountDetails(accountDisplayName: string) {
    const account = this.sleeveSimpleAccount.find(
      name => name.displayLabel == accountDisplayName,
    )
    if (account) {
      const accountDetailsPromise = this.createEditTransactionService
        .loadAccountDetails(account.id)
        .toPromise()
      const isCollateralBrokerPromise = this.createEditTransactionService
        .isCollateralBroker([account.id])
        .toPromise()
      return Promise.all([
        accountDetailsPromise,
        isCollateralBrokerPromise,
      ]).then(([accountDetails, isCollateralBrokerMap]) => {
        this.accountDetails = accountDetails
        this.sourceAccountId = accountDetails.accountId
        this._isSourceCollateral = isCollateralBrokerMap[account.id]
        // A concrete account is now resolved; clear the account-unresolved marks
        // before the parent re-validates.
        this._aggregateAccountUnresolved = false
        clearAllInvalidations(this.gridApi?.api)
        // Replay the lookup deferred while the account was unresolved (cusip entered first):
        // loadPricingInfo cascades into loadSecurityDetails and self-guards on the cusip/account.
        this.loadPricingInfo()
        this.sleeveAccountDetails.emit({
          accountDetails: this.accountDetails,
          isCollateral: this._isSourceCollateral,
        })
        if (this.gridApi) this.adjustCollateralBroker()
      })
    }
  }

  setAggAccountDisplayName() {
    const accountRowField = this.rowData?.find(row => row.id == 'sourceAccount')

    if (accountRowField) {
      accountRowField.isEditable = true
      accountRowField.value = ''

      if (this._positionData && this._sleeveSimpleAccount) {
        const account = this._sleeveSimpleAccount.find(
          name => name.id == this._positionData?.accountId,
        )

        if (account) {
          accountRowField.value = account.displayLabel
          this.loadAggAccountDetails(account.displayLabel)
        }
      }
    }
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }

  adjustCollateralBroker() {
    const collateralNode = this.gridApi?.api.getRowNode('collateralBroker')
    if (this._isSourceCollateral !== this._isDestinationCollateral) {
      if (!collateralNode) {
        const collateralNodeData = getCollateralBroker(
          this._isDestinationCollateral
            ? 'Destination Account Collateral Broker'
            : 'Source Account Collateral Broker',
          this._transaction?.dataMap?.collateralBroker ??
            this._positionData?.collateralBroker,
        ) as TransferMappingModel
        this.rowData.push(collateralNodeData)
        this.gridApi?.api.applyTransaction({
          add: [collateralNodeData],
        })
        if (!this._transaction?.dataMap?.collateralBroker) {
          this.valueChanged.emit()
        }
      }
    } else {
      if (collateralNode) {
        this.gridApi?.api.applyTransaction({
          remove: [collateralNode],
        })
        this.rowNodeMap.delete('collateralBroker')
        this.valueChanged.emit()
      }
    }
  }

  adjustExitFeeReceivable() {
    const exitFeeNode = this.gridApi?.api.getRowNode('exitFeeReceivable')
    const isLoan = Calculators.isBankOrMortgageLoan(this._security)
    if (isLoan && !exitFeeNode) {
      const rowData = getExitFeeReceivableRow(
        this._transaction?.dataMap?.exitFeeReceivable,
      )
      this.rowData.push(rowData)
      this.gridApi?.api.applyTransaction({ add: [rowData] })
      this.gridApi?.api.sizeColumnsToFit()
    } else if (!isLoan && exitFeeNode) {
      this.gridApi?.api.applyTransaction({ remove: [exitFeeNode] })
      this.rowData = this.rowData.filter(row => row.id !== 'exitFeeReceivable')
      this.rowNodeMap.delete('exitFeeReceivable')
      this.gridApi?.api.sizeColumnsToFit()
      this.valueChanged.emit()
    }
  }
}
